import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Wallet, TrendingUp, Coins, ArrowDownToLine, LifeBuoy,
  Megaphone, LogOut, ShieldCheck, Users, Inbox, Banknote, Settings, Menu, X, WalletCards, UserCircle,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";

const userNav = [
  { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { to: "/deposit", label: "Deposit", Icon: Wallet },
  { to: "/funding", label: "Contribution", Icon: TrendingUp },
  { to: "/rewards", label: "Rewards", Icon: Coins },
  { to: "/withdrawals", label: "Withdrawals", Icon: ArrowDownToLine },
  { to: "/wallet", label: "Wallet", Icon: WalletCards },
  { to: "/profile", label: "Profile", Icon: UserCircle },
  { to: "/support", label: "Support", Icon: LifeBuoy },
  { to: "/announcements", label: "Announcements", Icon: Megaphone },
];

const adminNav = [
  { to: "/admin", label: "Overview", Icon: ShieldCheck },
  { to: "/admin/users", label: "Users", Icon: Users },
  { to: "/admin/deposits", label: "Deposits", Icon: Wallet },
  { to: "/admin/withdrawals", label: "Withdrawals", Icon: Banknote },
  { to: "/admin/tickets", label: "Tickets", Icon: Inbox },
  { to: "/admin/announcements", label: "Announcements", Icon: Megaphone },
  { to: "/admin/settings", label: "Settings", Icon: Settings },
];

export default function AppLayout({ children, admin = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const links = admin ? adminNav : userNav;

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen text-white">
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 glass sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <Logo />
        </div>
        <div className="flex items-center gap-2">
          {!admin && <NotificationBell />}
          <ThemeToggle />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-white/5">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Mobile backdrop */}
        {open && (
          <div
            className="md:hidden fixed inset-0 z-20 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="sidebar-backdrop"
          />
        )}
        {/* Sidebar */}
        <aside
          className={`fixed md:sticky top-0 z-30 h-screen w-64 shrink-0 glass-strong p-5 border-r border-white/10 overflow-y-auto transform transition-transform duration-300 ease-out ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
          data-testid="sidebar"
        >
          <div className="hidden md:flex items-center justify-between mb-8">
            <Logo />
            <div className="flex items-center gap-2">
              {!admin && <NotificationBell align="left" />}
              <ThemeToggle />
            </div>
          </div>
          <nav className="flex flex-col gap-1.5">
            {links.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/admin"}
                onClick={() => setOpen(false)}
                className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={18} strokeWidth={1.6} />
                <span className="text-sm">{label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 pt-5 border-t border-white/10">
            <div className="text-xs uppercase tracking-[0.2em] text-[#475569] font-bold mb-3">Account</div>
            <div className="text-sm text-white/90 font-medium truncate">{user?.name}</div>
            <div className="text-xs text-[#94A3B8] truncate">{user?.email}</div>
            {user?.user_id && <div className="text-[11px] font-mono text-[#00F0FF] mb-3" data-testid="sidebar-user-id">ID: {user.user_id}</div>}
            {user?.role === "admin" && !admin && (
              <NavLink to="/admin" className="side-link mb-2" data-testid="goto-admin">
                <ShieldCheck size={16} /> <span className="text-sm">Admin Panel</span>
              </NavLink>
            )}
            {admin && (
              <NavLink to="/dashboard" className="side-link mb-2" data-testid="goto-user">
                <LayoutDashboard size={16} /> <span className="text-sm">User View</span>
              </NavLink>
            )}
            <button onClick={handleLogout} className="side-link w-full text-left" data-testid="logout-button">
              <LogOut size={16} /> <span className="text-sm">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-h-screen p-3 sm:p-4 md:p-8 max-w-full overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}

export function Logo() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#007AFF] flex items-center justify-center pulse-glow">
          <span className="font-display font-black text-[#060B19] text-lg">S</span>
        </div>
      </div>
      <div>
        <div className="font-display font-black text-[17px] leading-none neon-text">SpectralX</div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#94A3B8] mt-0.5">Client Portal</div>
      </div>
    </div>
  );
}
